import { useLineItemRequests } from "../../hooks/useLineItemRequests";
import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  EmptySearchResult,
  FormLayout,
  Icon,
  InlineStack,
  LegacyStack,
  Modal,
  Pagination,
  ResourceItem,
  ResourceList,
  Text,
} from "@shopify/polaris";
import { PlusCircleIcon, MinusCircleIcon } from "@shopify/polaris-icons";
import HorseVariant from "./HorseVariant/HorseVariant";
import { HorseVariantSearchFieldAndMoreFilters } from "../horse_variants/index/support/HorseVariantSearchFieldAndMoreFilters";
import type { DecimalRangeFilter, HorseVariantsQueryParams, LineItemVariant } from "../../api_utils/types";
import { getMatchingHorseVariants, useHorseVariants } from "../../api_utils/requests";
import ConfirmationDialog from "./ConfirmationDialog";

const emptyState = <EmptySearchResult title="No variants found" withIllustration />;

const defaultInitialQuantity: DecimalRangeFilter = { more_than: undefined, less_than: undefined };
const emptyArray: number[] = [];

export default function AddVariantDialog({
  active,
  selectedVariantIds,
  onAdd,
  onRemove,
  removalWarning,
  initialHorseLocationId,
  initialQuantity = defaultInitialQuantity,
  initialSupplierId,
  initialVendor,
  primary = false,
  disabled = false,
  setActive,
  setErrorMessage,
  excludableHorseVariantIds = emptyArray,
}: {
  readonly active: boolean;
  readonly selectedVariantIds: number[];
  readonly onAdd: (horseVariants: LineItemVariant[]) => void;
  readonly onRemove: (horseVariantIds: number[]) => void;
  readonly removalWarning?: (horseVariantIds: number[]) => React.ReactNode;
  readonly initialHorseLocationId?: number;
  readonly initialQuantity?: DecimalRangeFilter;
  readonly initialSupplierId?: number;
  readonly initialVendor?: string;
  readonly primary?: boolean;
  readonly disabled?: boolean;
  readonly setActive: (active: boolean) => void;
  readonly setErrorMessage: (errorMessage: string) => void;
  readonly excludableHorseVariantIds?: number[];
}): React.JSX.Element {
  const [filters, setFilters] = useState<HorseVariantsQueryParams>({});
  const [allItemsSelected, setAllItemsSelected] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [variantIdsToRemove, setVariantIdsToRemove] = useState<number[]>([]);
  const selectedIds = new Set(selectedVariantIds);
  const excludedIds = new Set(excludableHorseVariantIds);

  const handleSetFilters = useCallback((newFilters: HorseVariantsQueryParams) => {
    setAllItemsSelected(false);
    setFilters(newFilters);
    setPageNum(1);
  }, []);

  const {
    data: { rows, hasNext, hasPrev },
  } = useHorseVariants({ ...filters, page: pageNum });
  const horseVariants = rows.filter((horseVariant) => {
    return !excludedIds.has(horseVariant.id);
  });
  const allSelectedOnCurrentPage = horseVariants.length > 0 && horseVariants.every((horseVariant) => selectedIds.has(Number(horseVariant.id)));

  const { request, cancel } = useLineItemRequests(setErrorMessage);
  useEffect(() => {
    if (disabled) { cancel(); setIsConfirmRemoveOpen(false); }
  }, [disabled, cancel]);

  const addVariants = (variants: LineItemVariant[]): void => {
    const additions = variants.filter(({ id }) => !selectedIds.has(id) && !excludedIds.has(id));
    if (additions.length > 0) onAdd(additions);
  };

  const addAllOnCurrentPage = (): void => {
    addVariants(horseVariants);
  };

  const handleRemoveAllOnCurrentPage = (): void => {
    setVariantIdsToRemove(horseVariants.map(({ id }) => id));
    setIsConfirmRemoveOpen(true);
  };

  const handleRemoveAllVariants = (): void => {
    setVariantIdsToRemove(selectedVariantIds);
    setIsConfirmRemoveOpen(true);
  };

  const confirmRemoval = (): void => {
    onRemove(variantIdsToRemove);
    setAllItemsSelected(false);
    setIsConfirmRemoveOpen(false);
  };

  const addAllVariants = (): void => {
    setIsLoading(true);
    void request((signal) => getMatchingHorseVariants(filters, signal), (variants) => {
      addVariants(variants);
      setAllItemsSelected(true);
    }).finally(() => { setIsLoading(false); });
  };

  const addItem = (id: string): void => {
    if (isLoading || disabled) return;
    const horseVariantId = Number(id);
    if (selectedIds.has(horseVariantId)) {
      if (removalWarning?.([horseVariantId])) {
        setVariantIdsToRemove([horseVariantId]);
        setIsConfirmRemoveOpen(true);
      } else {
        onRemove([horseVariantId]);
        setAllItemsSelected(false);
      }
    } else {
      addVariants(horseVariants.filter(({ id: variantId }) => variantId === horseVariantId));
    }
  };

  const renderItem = (horseVariant: LineItemVariant): React.ReactElement => {
    return (
      <ResourceItem
        accessibilityLabel={`View details for ${horseVariant.id}`}
        id={horseVariant.id.toString()}
        media={<Icon source={selectedIds.has(horseVariant.id) ? MinusCircleIcon : PlusCircleIcon} />}
        onClick={addItem}
        verticalAlignment="center"
      >
        <HorseVariant disableClick showAvailable variant={horseVariant} />
      </ResourceItem>
    );
  };

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementById("variant-header")?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const handleOpen = useCallback(() => {
    setAllItemsSelected(false);
    setActive(true);
  }, [setActive]);
  const handleClose = useCallback(() => {
    if (isLoading) return;
    setActive(false);
  }, [isLoading, setActive]);
  const handleCloseConfirmation = useCallback(() => {
    setIsConfirmRemoveOpen(false);
  }, []);

  return (
    <Modal
      activator={
        <Button disabled={disabled} onClick={handleOpen} variant={primary ? "primary" : "secondary"}>
          Add variants
        </Button>
      }
      limitHeight
      onClose={handleClose}
      open={active}
      primaryAction={{
        content: "Close",
        disabled: isLoading,
        onAction: handleClose,
      }}
      title="Add variants"
    >
      <ConfirmationDialog
        active={isConfirmRemoveOpen}
        content={
          (isConfirmRemoveOpen && removalWarning?.(variantIdsToRemove)) || (variantIdsToRemove.length > 0
            ? `Are you sure that you want to remove ${variantIdsToRemove.length} variants?`
            : "Are you sure that you want to remove all variants?")
        }
        isDestructive
        okText={variantIdsToRemove.length === 1 ? "Remove variant" : "Remove variants"}
        onCancel={handleCloseConfirmation}
        onClose={handleCloseConfirmation}
        onOk={confirmRemoval}
        title={variantIdsToRemove.length === 1 ? "Remove variant?" : "Remove variants?"}
      />
      <Modal.Section>
        <FormLayout>
          <FormLayout.Group>
            <HorseVariantSearchFieldAndMoreFilters
              disableTabs
              disabled={isLoading || disabled}
              initialHorseLocationId={initialHorseLocationId}
              initialQuantity={initialQuantity}
              initialSupplierId={initialSupplierId}
              initialVendor={initialVendor}
              setFilters={handleSetFilters}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Box id="variant-header" paddingBlockEnd="400">
              <InlineStack blockAlign="center" gap="300">
                <Button
                  accessibilityLabel={allSelectedOnCurrentPage || allItemsSelected ? "Remove all variants on this page" : "Add all variants on this page"}
                  disabled={isLoading || disabled}
                  icon={allSelectedOnCurrentPage || allItemsSelected ? MinusCircleIcon : PlusCircleIcon}
                  onClick={allSelectedOnCurrentPage || allItemsSelected ? handleRemoveAllOnCurrentPage : addAllOnCurrentPage}
                  variant="plain"
                />
                {allSelectedOnCurrentPage || allItemsSelected ? (
                  <Button disabled={isLoading || disabled} onClick={allItemsSelected ? handleRemoveAllVariants : addAllVariants} variant="plain">
                    {allItemsSelected ? "Remove all variants" : "Add all variants that match this search"}
                  </Button>
                ) : (
                  <Text as="span">{`${selectedIds.size} added`}</Text>
                )}
              </InlineStack>
            </Box>
          </FormLayout.Group>
        </FormLayout>
        <ResourceList
          emptyState={emptyState}
          items={horseVariants}
          loading={isLoading}
          renderItem={renderItem}
          resourceName={{
            singular: "variant",
            plural: "variants",
          }}
        />
      </Modal.Section>
      <Modal.Section>
        <LegacyStack alignment="center" distribution="center">
          <Pagination
            hasNext={hasNext && !isLoading ? !disabled : null}
            hasPrevious={hasPrev && !isLoading ? !disabled : null}
            nextKeys={[39]}
            onNext={nextPage}
            onPrevious={prevPage}
            previousKeys={[37]}
          />
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
}
